import { displayImportedText } from '../services/displayLabels';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, UNSAFE_NavigationContext, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { measurementsApi, patientsApi, postVisitTasksApi, visitsApi } from '../services/api';
import { BiaAnalyzer } from './BiaAnalyzer';
import './clinic.css';
import VisitBilling from './VisitBilling';
import { VisitContinuity, VisitPatientSummary } from './VisitContinuity';
import { earlierVisits, orderedVisits } from './visitComparison';

const toInputDateTime = value => {
  const date = value ? new Date(value) : new Date();
  const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return shifted.toISOString().slice(0, 16);
};
const numberOrNull = value => value === '' || value == null ? null : Number(value);
const addDays = (value, days) => { const date = value ? new Date(value) : new Date(); date.setDate(date.getDate() + days); return date; };
const toInputDate = value => {
  const date = value ? new Date(value) : new Date();
  const shifted = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
  return shifted.toISOString().slice(0, 10);
};
const formatDateTime = value => value ? new Date(value).toLocaleString('it-IT', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

function newVisit(hasHistory) {
  return {
    startsAt: toInputDateTime(),
    completedAt: null,
    type: hasHistory ? 'Controllo' : 'Prima visita',
    status: 'In corso',
    reason: '',
    weightKg: '',
    waistCircumferenceCm: '',
    hipCircumferenceCm: '',
    clinicalNotes: '',
    nextSteps: ''
  };
}

export function VisitWorkspace() {
  const { id: patientId, visitId: routeVisitId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const copilotVisit = location.state?.copilotVisit;
  const [patient, setPatient] = useState(null);
  const [visits, setVisits] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [draft, setDraft] = useState(null);
  const [currentVisitId, setCurrentVisitId] = useState(routeVisitId || '');
  const [showBia, setShowBia] = useState(searchParams.get('bia') === '1' && Boolean(routeVisitId));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveError, setSaveError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [referenceId, setReferenceId] = useState(location.state?.previousVisitId || '');
  const [savedVisit, setSavedVisit] = useState(null);
  const [showSummary, setShowSummary] = useState(false);
  const [step, setStep] = useState(searchParams.get('bia') === '1' ? 1 : 0);
  const stepRef = useRef(null);
  const formRef = useRef(null);
  const summaryRef = useRef(null);
  const [postVisit, setPostVisit] = useState(() => ({
    createDietTask: true,
    dietDueOn: toInputDate(addDays(new Date(), 3)),
    createFollowUpAppointment: true,
    nextControlAt: toInputDateTime(addDays(new Date(), 30)),
    nextControlDurationMinutes: 55
  }));

  const load = useCallback(async (isCurrent = () => true) => {
    setLoading(true);
    setError('');
    setSaveError('');
    setPatient(null);
    setDraft(null);
    try {
      const [patientData, visitItems, measurementItems] = await Promise.all([
        patientsApi.get(patientId),
        visitsApi.getAll(patientId, 200),
        measurementsApi.getByPatient(patientId, 50)
      ]);
      if (!isCurrent()) return;
      setPatient(patientData);
      setVisits(visitItems || []);
      setMeasurements(measurementItems || []);
      if (routeVisitId) {
        const existing = await visitsApi.get(routeVisitId);
        if (!isCurrent()) return;
        if (existing.patientId !== patientId) throw new Error('La visita non appartiene al paziente selezionato.');
        setDraft({ ...existing, startsAt: toInputDateTime(existing.startsAt) });
        setCurrentVisitId(existing.id);
        setSavedVisit(existing);
      } else {
        const visitDraft = newVisit(Boolean(visitItems?.length || measurementItems?.length));
        setDraft(copilotVisit ? { ...visitDraft, ...copilotVisit } : visitDraft);
        setCurrentVisitId('');
        setSavedVisit(null);
      }
      setDirty(Boolean(!routeVisitId && copilotVisit));
      setShowSummary(false);
    } catch (loadError) {
      if (isCurrent()) setError(loadError.message || 'Visita non disponibile.');
    } finally {
      if (isCurrent()) setLoading(false);
    }
  }, [patientId, routeVisitId, copilotVisit]);

  useEffect(() => { let current = true; load(() => current); return () => { current = false; }; }, [load]);

  useEffect(() => {
    if (!dirty) return undefined;
    const preventUnload = event => { event.preventDefault(); event.returnValue = ''; };
    window.addEventListener('beforeunload', preventUnload);
    return () => window.removeEventListener('beforeunload', preventUnload);
  }, [dirty]);

  useEffect(() => {
    if (!dirty || saving || typeof navigator?.block !== 'function') return undefined;
    const unblock = navigator.block(transition => {
      if (window.confirm('La visita contiene modifiche non salvate. Vuoi uscire e scartarle?')) { unblock(); transition.retry(); }
    });
    return unblock;
  }, [dirty, saving, navigator]);

  const latestMeasurement = measurements[0];
  const previousVisits = earlierVisits(visits, currentVisitId, draft?.startsAt);
  const previousVisit = previousVisits.find(visit => visit.id === referenceId) || previousVisits[0];
  const change = event => {
    const { name, value } = event.target;
    setDraft(current => ({ ...current, [name]: value }));
    setDirty(true);
    setShowSummary(false);
  };
  const reusePreviousPlan = () => {
    if (!previousVisit?.nextSteps?.trim()) return;
    if (draft.nextSteps?.trim() && !window.confirm('Sostituire le indicazioni correnti con quelle della visita di riferimento?')) return;
    setDraft(value => ({ ...value, nextSteps: previousVisit.nextSteps }));
    setDirty(true);
    setShowSummary(false);
    setMessage(`Indicazioni riprese dalla visita del ${formatDateTime(previousVisit.startsAt)}. Rivedile e salva quando sono aggiornate.`);
    setStep(2);
    requestAnimationFrame(() => formRef.current?.querySelector('[name="nextSteps"]')?.focus());
  };
  const changePostVisit = event => {
    const { name, type, checked, value } = event.target;
    setPostVisit(current => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
  };

  useEffect(() => {
    if (!draft?.startsAt) return;
    setPostVisit(current => {
      if (current.nextControlTouched) return current;
      return { ...current, nextControlAt: toInputDateTime(addDays(draft.startsAt, 30)) };
    });
  }, [draft?.startsAt]);

  const payload = useCallback((status = draft?.status, completedAt = draft?.completedAt) => ({
    patientId,
    startsAt: new Date(draft.startsAt).toISOString(),
    completedAt,
    type: draft.type,
    status,
    reason: draft.reason || null,
    weightKg: numberOrNull(draft.weightKg),
    waistCircumferenceCm: numberOrNull(draft.waistCircumferenceCm),
    hipCircumferenceCm: numberOrNull(draft.hipCircumferenceCm),
    clinicalNotes: draft.clinicalNotes || null,
    nextSteps: draft.nextSteps || null
  }), [draft, patientId]);

  const persistVisit = async (status = draft.status, completedAt = draft.completedAt) => {
    if (saving) return null;
    const invalid = formRef.current?.querySelector('input:invalid, select:invalid, textarea:invalid');
    if (invalid) {
      setStep(Number(invalid.closest('[data-visit-step]').dataset.visitStep));
      requestAnimationFrame(() => invalid.reportValidity());
      return null;
    }
    if (!draft.startsAt || !Number.isFinite(Date.parse(draft.startsAt))) {
      setError('Inserisci una data e un’ora valide.');
      return null;
    }
    setSaving(true);
    setError('');
    setSaveError('');
    setMessage('');
    try {
      const saved = currentVisitId
        ? await visitsApi.update(currentVisitId, payload(status, completedAt))
        : await visitsApi.create(payload(status, completedAt));
      setCurrentVisitId(saved.id);
      setDraft({ ...saved, startsAt: toInputDateTime(saved.startsAt) });
      setSavedVisit(saved);
      setDirty(false);
      setVisits(items => orderedVisits([saved, ...items.filter(item => item.id !== saved.id)]));
      if (!currentVisitId) navigate(`/patients/${patientId}/visits/${saved.id}`, { replace: true });
      setMessage(status === 'Completata' ? 'Visita conclusa e salvata.' : 'Visita salvata.');
      return saved;
    } catch (saveError) {
      setError(saveError.message);
      setSaveError(saveError.message || 'Salvataggio non riuscito.');
      return null;
    } finally {
      setSaving(false);
    }
  };

  const startBia = async () => {
    if (!draft.weightKg || Number(draft.weightKg) <= 0) {
      setStep(1);
      setError('Inserisci il peso rilevato prima di avviare la BIA.');
      requestAnimationFrame(() => formRef.current?.querySelector('[name="weightKg"]')?.focus());
      return;
    }
    const visit = await persistVisit('In corso', null);
    if (visit) { setStep(1); setShowBia(true); requestAnimationFrame(() => document.getElementById('acquisizione-bia')?.scrollIntoView({ block: 'start' })); }
  };

  const completeVisit = async () => {
    const saved = await persistVisit('Completata', new Date().toISOString());
    if (!saved) return;
    if (!postVisit.createDietTask && !postVisit.createFollowUpAppointment) {
      setMessage('Visita conclusa. Nessun lavoro post-visita aperto.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      const workflow = await postVisitTasksApi.prepare({
        visitId: saved.id,
        createDietTask: postVisit.createDietTask,
        dietDueOn: postVisit.createDietTask ? postVisit.dietDueOn : null,
        createFollowUpAppointment: postVisit.createFollowUpAppointment,
        nextControlAt: postVisit.createFollowUpAppointment ? new Date(postVisit.nextControlAt).toISOString() : null,
        nextControlDurationMinutes: Number(postVisit.nextControlDurationMinutes) || 55
      });
      setMessage(workflow?.message || 'Visita conclusa e post-visita preparato.');
    } catch (workflowError) {
      setError(workflowError.message || 'Visita conclusa, ma non sono riuscito a preparare il post-visita.');
    } finally {
      setSaving(false);
    }
  };

  const onMeasurementSaved = saved => {
    setMeasurements(items => [saved, ...items]);
    setDraft(value => ({ ...value, weightKg: saved.weightKg, measurementCount: (value.measurementCount || 0) + 1 }));
    setDirty(true);
    setShowSummary(false);
    setMessage('BIA acquisita e associata alla visita.');
  };

  const goToStep = index => {
    setStep(index);
    requestAnimationFrame(() => { stepRef.current?.focus(); stepRef.current?.scrollIntoView({ block: 'start' }); });
  };

  const visitTitle = useMemo(() => {
    if (!draft) return 'Visita';
    return currentVisitId ? `${draft.type} del ${formatDateTime(draft.startsAt)}` : 'Nuova visita';
  }, [draft, currentVisitId]);

  if (loading) return <div className="clinic-page"><div className="clinic-empty" role="status">Preparazione della visita…</div></div>;
  if (!patient || !draft) return <div className="clinic-page"><div className="alert alert-danger" role="alert">{error || 'Visita non disponibile.'}</div><button className="clinic-button" onClick={() => load()}>Riprova</button><Link className="clinic-button secondary" to={`/patients/${patientId}`}>Torna alla cartella</Link></div>;

  return <div className="clinic-page visit-workspace">
    <header className="clinic-head visit-head">
      <div className="workspace-summary">
        <span className="clinic-avatar">{patient.firstName?.[0]}{patient.lastName?.[0]}</span>
        <div>
          <h1>{visitTitle}</h1>
          <p><strong>{patient.lastName} {patient.firstName}</strong> · {displayImportedText(patient.code) || 'Senza codice'} · {draft.status}</p>
        </div>
      </div>
      <Link className="clinic-button secondary" to={`/patients/${patientId}`} onClick={event => { if (dirty && typeof navigator?.block !== 'function' && !window.confirm('La visita contiene modifiche non salvate. Vuoi uscire e scartarle?')) event.preventDefault(); }}>Torna alla cartella</Link>
    </header>

    {error && <div className="alert alert-danger" role="alert">{error}</div>}
    {message && <div className="alert alert-success" role="status">{message}</div>}

    <nav className="visit-step-nav" aria-label="Percorso della visita">
      {['Visita', 'Rilevazioni e BIA', 'Valutazione e piano', 'Chiusura'].map((label, index) => <button key={label} type="button" aria-current={step === index ? 'step' : undefined} onClick={() => goToStep(index)}><span>{index + 1}</span>{label}</button>)}
    </nav>
    <p className="visit-step-hint">Passaggio {step + 1} di 4 · Puoi tornare ai passaggi precedenti senza perdere i dati. Il salvataggio è esplicito.</p>

    <div className="visit-layout">
      <main className="visit-main" ref={stepRef} tabIndex={-1}>

        <form ref={formRef} onSubmit={event => { event.preventDefault(); persistVisit(); }}>
        <fieldset disabled={saving} style={{ border:0, padding:0, margin:0, minWidth:0 }}>
        <section hidden={step !== 0} data-visit-step="0" className="clinic-panel clinic-form visit-form-section">
          <div className="clinic-panel-head"><div><h2>Dati della visita</h2><p>Conferma data, tipo e motivo dell’incontro. Consulta l’anamnesi nella cartella quando serve.</p></div></div>
          <div className="clinic-grid three">
            <label>Data e ora<input name="startsAt" type="datetime-local" required value={draft.startsAt} onChange={change}/></label>
            <label>Tipo di visita<select name="type" value={draft.type} onChange={change}><option>Prima visita</option><option>Controllo</option><option>Follow-up remoto</option></select></label>
            <label>Motivo della visita<input name="reason" maxLength={500} value={draft.reason || ''} onChange={change} placeholder="Es. controllo composizione corporea"/></label>
          </div>
        </section>
        <section hidden={step !== 1} data-visit-step="1" className="clinic-panel clinic-form visit-form-section">
          <div className="clinic-panel-head"><div><h2>Rilevazioni di oggi</h2><p>Inserisci il peso e le circonferenze rilevati, poi prosegui con BioSmart. Le misure precedenti non vengono copiate.</p></div></div>
          <div className="clinic-grid three">
            <label>Peso rilevato (kg)<input name="weightKg" type="number" min="5" max="300" step="0.1" value={draft.weightKg ?? ''} onChange={change}/></label>
            <label>Circonferenza vita (cm)<input name="waistCircumferenceCm" type="number" min="20" max="250" step="0.1" value={draft.waistCircumferenceCm ?? ''} onChange={change}/></label>
            <label>Circonferenza fianchi (cm)<input name="hipCircumferenceCm" type="number" min="20" max="250" step="0.1" value={draft.hipCircumferenceCm ?? ''} onChange={change}/></label>
          </div>
        </section>

        <section hidden={step !== 2} data-visit-step="2" className="clinic-panel clinic-form visit-form-section">
          <div className="clinic-panel-head"><div><h2>Valutazione e piano</h2><p>Annotazioni riferite esclusivamente a questo incontro.</p></div></div>
          <div className="clinic-grid two">
            <label>Valutazione clinica riservata<textarea name="clinicalNotes" maxLength={8000} value={draft.clinicalNotes || ''} onChange={change} placeholder="Osservazioni, aderenza, sintomi ed elementi emersi durante la visita…"/></label>
            <label>Indicazioni e prossimi passi<textarea name="nextSteps" maxLength={4000} value={draft.nextSteps || ''} onChange={change} placeholder="Indicazioni da condividere con il paziente…"/><small>Queste indicazioni compaiono nel riepilogo paziente.</small></label>
          </div>
        </section>
        </fieldset>
        </form>

        <div hidden={step !== 1} className="visit-measure-step">
        <div className="visit-action-block">
          <strong>Bioimpedenziometria</strong>
          <p>{draft.measurementCount ? `${draft.measurementCount} BIA associata alla visita.` : 'Nessuna BIA associata alla visita.'}</p>
          <button className="clinic-button visit-primary-action" onClick={startBia} disabled={saving}>{showBia ? 'Torna all’acquisizione BIA' : 'Avvia nuova BIA'}</button>
        </div>
    {showBia && currentVisitId && <section className="visit-bia-section" id="acquisizione-bia">
      <div className="visit-bia-heading"><div><h2>Acquisizione BIA</h2><p>Il paziente e la visita sono già selezionati. Collega BioSmart e avvia la lettura.</p></div><button className="clinic-button secondary" onClick={() => setShowBia(false)}>Chiudi acquisizione</button></div>
      <BiaAnalyzer patientId={patientId} visitId={currentVisitId} initialWeightKg={draft.weightKg} embedded onMeasurementSaved={onMeasurementSaved}/>
    </section>}
        </div>
        <div hidden={step !== 3} id="chiusura-visita" className="clinic-panel clinic-form visit-close-step">
          <h2>Concludi e prepara il seguito</h2>
          <p>Controlla le indicazioni, scegli cosa preparare dopo la visita e conferma la chiusura.</p>
          <dl className="visit-close-summary"><div><dt>Visita</dt><dd>{draft.type} · {formatDateTime(draft.startsAt)}</dd></div><div><dt>Peso rilevato</dt><dd>{draft.weightKg ? `${Number(draft.weightKg).toLocaleString('it-IT')} kg` : 'Non rilevato'}</dd></div><div><dt>BIA della visita</dt><dd>{draft.measurementCount ? `${draft.measurementCount} acquisita/e` : 'Non acquisita'}</dd></div><div><dt>Indicazioni per il paziente</dt><dd>{draft.nextSteps || 'Nessuna indicazione inserita'}</dd></div></dl>
        <div className="visit-action-block post-visit-setup">
          <strong>Dopo la visita</strong>
          <p>Sibilla prepara i lavori che di solito partono appena il paziente va via.</p>
          <label className="post-visit-choice"><input type="checkbox" name="createDietTask" checked={postVisit.createDietTask} onChange={changePostVisit}/> <span>Piano alimentare da consegnare</span></label>
          {postVisit.createDietTask && <label className="post-visit-date">Consegna entro<input type="date" name="dietDueOn" value={postVisit.dietDueOn} onChange={changePostVisit}/></label>}
          <label className="post-visit-choice"><input type="checkbox" name="createFollowUpAppointment" checked={postVisit.createFollowUpAppointment} onChange={changePostVisit}/> <span>Proponi prossimo controllo</span></label>
          {postVisit.createFollowUpAppointment && <><label className="post-visit-date">Data controllo<input type="datetime-local" name="nextControlAt" value={postVisit.nextControlAt} onChange={event => { changePostVisit(event); setPostVisit(current => ({ ...current, nextControlTouched: true })); }}/></label><label className="post-visit-date">Durata minuti<input type="number" min="10" max="480" step="5" name="nextControlDurationMinutes" value={postVisit.nextControlDurationMinutes} onChange={changePostVisit}/></label></>}
        </div>
        <button className="clinic-button visit-complete" onClick={completeVisit} disabled={saving || draft.status === 'Completata'}>{saving ? 'Sto chiudendo…' : 'Concludi visita e prepara il seguito'}</button>
        <p className="visit-save-note">La BIA resta collegata alla visita. Il piano e il controllo entrano nella coda di lavoro.</p>

        </div>
        <div hidden={step !== 3}>
          {currentVisitId && <details className="visit-reference"><summary>Fatturazione della visita</summary>
    {currentVisitId && <VisitBilling key={currentVisitId} visitId={currentVisitId} patient={patient} visitStatus={draft.status} visitType={draft.type} />}
          </details>}
        </div>
        <details className="visit-reference" key={step}>
          <summary>{step === 0 ? 'Consulta anamnesi e visite precedenti' : 'Confronta con la visita precedente'}</summary>
    <section className="visit-context" aria-label="Contesto clinico">
      <div><span>Ultima BIA</span><strong>{latestMeasurement ? formatDateTime(latestMeasurement.measuredAt) : 'Nessuna'}</strong></div>
      <div><span>Ultimo peso</span><strong>{latestMeasurement?.weightKg ? `${latestMeasurement.weightKg.toFixed(1)} kg` : '—'}</strong></div>
      <div><span>Ultimo BMI</span><strong>{latestMeasurement?.bodyComposition?.bmi?.toFixed(1) || '—'}</strong></div>
      <div><span>Visita precedente</span><strong>{previousVisit ? formatDateTime(previousVisit.startsAt) : 'Nessuna'}</strong></div>
    </section>

        <VisitContinuity patient={patient} previousVisits={previousVisits} previousVisit={previousVisit} onSelect={setReferenceId} draft={draft} onReuse={reusePreviousPlan} disabled={saving}/>
        </details>
        <div className="visit-step-footer">
          {step > 0 && <button className="clinic-button secondary" onClick={() => goToStep(step - 1)}>Indietro</button>}
          {step < 3 && <button className="clinic-button" onClick={() => goToStep(step + 1)}>{['Prosegui alle rilevazioni', 'Prosegui alla valutazione', 'Prosegui alla chiusura'][step]}</button>}
        </div>
      </main>

      <aside id="azioni-visita" className="visit-actions" aria-label="Azioni della visita">
        <div className="visit-actions-head"><h2>Azioni della visita</h2><span className={`visit-status ${draft.status === 'Completata' ? 'complete' : ''}`}>{draft.status}</span></div>
        <button className="clinic-button secondary" onClick={() => persistVisit()} disabled={saving}>{saving ? 'Salvataggio…' : 'Salva visita'}</button>
        {saveError && <div className="visit-save-error"><p>{saveError}</p><span>I dati inseriti sono ancora qui. Premi “Salva visita” per riprovare.</span></div>}
        <p className="visit-save-state" role="status">{dirty ? 'Modifiche da salvare' : currentVisitId ? 'Visita salvata' : 'Bozza non salvata'}</p>
        <button className="clinic-button secondary" disabled={!savedVisit || dirty || saving} onClick={() => { setShowSummary(true); requestAnimationFrame(() => summaryRef.current?.focus()); }}>Riepilogo paziente</button>
        {(!savedVisit || dirty) && <small>Salva la visita per preparare il riepilogo.</small>}
      </aside>
    </div>

    {showSummary && savedVisit && <div ref={summaryRef} tabIndex={-1}><VisitPatientSummary patient={patient} visit={savedVisit} onClose={() => setShowSummary(false)} onPrint={() => window.print()}/></div>}

  </div>;
}

export default VisitWorkspace;
